/**
 * A marca da UpOwl.
 *
 * O QUE ESTAVA AQUI ANTES
 *
 * Um SVG desenhado à mão — quadrado arredondado, dois olhos circulares, um
 * sorriso — justificado com *"inline SVG so we don't depend on external
 * files"*. O objetivo era razoável; o caminho, não. Aquele desenho **não é a
 * coruja da UpOwl**, e estava em produção no cabeçalho e na tela de login.
 *
 * A marca real está em `uw-dashboard-react/src/assets/`: uma coruja em traço,
 * estilo circuito, com "UPOWL TECHNOLOGIES" em serifada **vetorizada dentro
 * do arquivo**. Por isso o texto não é desenhado em CSS aqui: ele já vem no
 * SVG, e reescrevê-lo em Oswald daria duas tipografias para a mesma palavra.
 *
 * Os arquivos foram copiados para `src/assets/marca/` — é assim que se evita
 * depender de repositório vizinho sem redesenhar nada.
 *
 * O VIEWBOX DOS ORIGINAIS ESTÁ ERRADO
 *
 * As quatro peças com texto declaram uma janela menor que o desenho, e parte
 * de "TECHNOLOGIES" fica fora. Medido com `getBBox()`:
 *
 *     horizontal   viewBox 2009×318   desenho 2471×318   18,7% cortado
 *     vertical     viewBox  640×421   desenho  682×432    6,1% cortado
 *
 * Corrigido nas nossas cópias — só a janela, o desenho é o mesmo. Por isso as
 * proporções abaixo usam 2471 e 682, não os números do arquivo original.
 *
 * QUAL PEÇA EM QUAL LUGAR
 *
 * Cada variante é **uma cor chapada** (a marca não tem gradiente); a escolha
 * é só de contraste. A orientação é de espaço: a horizontal tem quase 8:1 e
 * não cabe numa barra lateral de 240px.
 *
 *                 fundo claro                     fundo escuro
 *   símbolo       logo-charcoal.svg               logo-white.svg
 *   horizontal    logo-with-text-horizontal       ...-white-horizontal
 *   vertical      logo-with-text                  logo-with-text-white
 *
 * Ver `docs/padrao/design-system.md`.
 */
import { Box } from "@mui/material";

import simboloEscuro from "../assets/marca/logo-charcoal.svg";
import simboloClaro from "../assets/marca/logo-white.svg";
import horizontalEscura from "../assets/marca/logo-with-text-horizontal.svg";
import horizontalClara from "../assets/marca/logo-with-text-white-horizontal.svg";
import verticalEscura from "../assets/marca/logo-with-text.svg";
import verticalClara from "../assets/marca/logo-with-text-white.svg";

export type LayoutDaMarca = "simbolo" | "horizontal" | "vertical";

interface Props {
  /** Altura da peça, em px. A largura sai da proporção do desenho. */
  size?: number;
  /** `light` = sobre fundo escuro (usa a arte branca). */
  variant?: "default" | "light";
  /** Espaço disponível manda: horizontal é ~7,8:1. */
  layout?: LayoutDaMarca;
}

// Proporções medidas, não as declaradas no arquivo de origem.
const PECA = {
  simbolo: { claro: simboloClaro, escuro: simboloEscuro, proporcao: 640.03625 / 317.76346 },
  horizontal: { claro: horizontalClara, escuro: horizontalEscura, proporcao: 2013 / 317.76346 },
  vertical: { claro: verticalClara, escuro: verticalEscura, proporcao: 640.03625 / 421.08097 },
} as const;

export function BrandMark({
  size = 36,
  variant = "default",
  layout = "horizontal",
}: Props) {
  const peca = PECA[layout];
  const arte = variant === "light" ? peca.claro : peca.escuro;

  return (
    <Box
      component="img"
      src={arte}
      alt="UpOwl"
      sx={{
        height: size,
        width: size * peca.proporcao,
        // Se o contêiner for mais estreito que a peça, encolhe junto em vez
        // de vazar — foi o que cortou a marca na barra lateral de 240px.
        maxWidth: "100%",
        objectFit: "contain",
        objectPosition: "left center",
        flexShrink: 0,
        display: "block",
      }}
    />
  );
}
